import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Play, CircleNotch, CheckCircle, XCircle } from "@phosphor-icons/react";

const SOURCES = [
  { key: "google", label: "Google" },
  { key: "inpi", label: "INPI" },
  { key: "instagram", label: "Instagram" },
  { key: "facebook", label: "Facebook" },
  { key: "linkedin", label: "LinkedIn" },
  { key: "youtube", label: "YouTube" },
  { key: "google_maps", label: "Google Maps" },
  { key: "domains", label: "Domínios" },
  { key: "images", label: "Busca por Imagens" },
];

const STATUS_MAP = {
  aguardando: { label: "Aguardando", color: "text-gray-600" },
  pesquisando: { label: "Pesquisando", color: "text-blue-600" },
  analisando: { label: "Analisando", color: "text-amber-600" },
  concluido: { label: "Concluído", color: "text-emerald-600" },
  concluido_com_avisos: { label: "Concluído (com aviso)", color: "text-amber-600" },
  falhou: { label: "Falhou", color: "text-red-600" },
};

export default function Scan() {
  const [brands, setBrands] = useState([]);
  const [brandId, setBrandId] = useState("");
  const [selected, setSelected] = useState(SOURCES.map(s => s.key));
  const [period, setPeriod] = useState("30d");
  const [maxQueries, setMaxQueries] = useState(3);
  const [autoVisual, setAutoVisual] = useState(false);
  const [scans, setScans] = useState([]);

  const load = async () => {
    const [{ data: b }, { data: s }] = await Promise.all([api.get("/brands"), api.get("/scans")]);
    setBrands(b);
    setScans(s);
    if (!brandId && b.length) setBrandId(b[0].id);
  };
  useEffect(() => { load(); const t = setInterval(load, 3000); return () => clearInterval(t); }, []);

  const toggle = (k) => setSelected(sel => sel.includes(k) ? sel.filter(x => x !== k) : [...sel, k]);

  const start = async () => {
    if (!brandId) { toast.error("Selecione uma marca"); return; }
    if (!selected.length) { toast.error("Selecione ao menos uma fonte"); return; }
    try {
      await api.post("/scans", { brand_id: brandId, sources: selected, period, max_queries_per_source: maxQueries, auto_visual: autoVisual });
      toast.success("Varredura iniciada");
      load();
    } catch (e) { toast.error("Falha ao iniciar varredura"); }
  };

  return (
    <div className="space-y-6" data-testid="scan-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Nova varredura</div>
        <h1 className="font-heading font-extrabold text-4xl">Iniciar nova varredura</h1>
      </div>

      <Card className="p-6 rounded-sm border">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div>
            <Label className="text-xs uppercase tracking-wider">Marca monitorada</Label>
            <Select value={brandId} onValueChange={setBrandId}>
              <SelectTrigger data-testid="scan-brand-select" className="rounded-sm mt-1"><SelectValue placeholder="Selecione" /></SelectTrigger>
              <SelectContent>
                {brands.map(b => <SelectItem key={b.id} value={b.id}>{b.nome_marca}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Período</Label>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="7d">Últimos 7 dias</SelectItem>
                <SelectItem value="30d">Últimos 30 dias</SelectItem>
                <SelectItem value="90d">Últimos 90 dias</SelectItem>
                <SelectItem value="all">Sem limite</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Consultas/fonte (SerpApi)</Label>
            <Select value={String(maxQueries)} onValueChange={(v) => setMaxQueries(parseInt(v))}>
              <SelectTrigger data-testid="max-queries-select" className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1 (mais econômico)</SelectItem>
                <SelectItem value="2">2</SelectItem>
                <SelectItem value="3">3 (recomendado)</SelectItem>
                <SelectItem value="5">5</SelectItem>
                <SelectItem value="10">10 (varredura ampla)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button data-testid="start-scan-btn" onClick={start} className="w-full rounded-sm bg-[#0A3B2C] hover:bg-[#083527]">
              <Play size={16} className="mr-2" /> Iniciar nova varredura
            </Button>
          </div>
        </div>

        <div>
          <Label className="text-xs uppercase tracking-wider mb-2 block">Fontes de pesquisa</Label>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {SOURCES.map(s => (
              <label key={s.key} data-testid={`source-${s.key}`} className={`flex items-center gap-2 p-3 border rounded-sm cursor-pointer ${selected.includes(s.key) ? "border-[#0A3B2C] bg-[#F3F4F6]" : ""}`}>
                <Checkbox checked={selected.includes(s.key)} onCheckedChange={() => toggle(s.key)} />
                <span className="text-sm">{s.label}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="mt-4 p-3 bg-[#F3F4F6] border-l-4 border-[#B45309] text-xs">
          <strong>Fontes reais:</strong> Google Search, Google Maps e Google Images (via SerpApi). Demais fontes ainda em modo demonstrativo até integração.
        </div>

        {selected.includes("images") && (
          <label className="mt-3 flex items-start gap-2 p-3 border rounded-sm cursor-pointer bg-[#F9FAFB]" data-testid="auto-visual-toggle">
            <Checkbox checked={autoVisual} onCheckedChange={setAutoVisual} />
            <div>
              <div className="text-sm font-semibold">Analisar visualmente automaticamente</div>
              <div className="text-xs text-muted-foreground">Ao final da varredura, compara cada logo capturado no Google Images com o logo oficial (CLIP + pHash + cor). Sem custo externo. Não gera novas buscas.</div>
            </div>
          </label>
        )}
      </Card>

      <Card className="p-6 rounded-sm border">
        <h3 className="font-heading font-bold text-lg mb-4">Histórico de varreduras</h3>
        <div className="space-y-2">
          {scans.slice(0, 15).map(s => {
            const st = STATUS_MAP[s.status] || STATUS_MAP.aguardando;
            const brand = brands.find(b => b.id === s.brand_id);
            const Icon = s.status === "concluido" ? CheckCircle : s.status === "falhou" ? XCircle : CircleNotch;
            return (
              <div key={s.id} data-testid={`scan-row-${s.id}`} className="flex items-center justify-between p-3 border rounded-sm hover:bg-muted/40">
                <div className="flex items-center gap-3">
                  <Icon size={18} className={`${st.color} ${s.status !== "concluido" && s.status !== "falhou" && s.status !== "concluido_com_avisos" ? "animate-spin" : ""}`} weight="bold" />
                  <div>
                    <div className="text-sm font-semibold">{brand?.nome_marca || "—"}</div>
                    <div className="text-xs text-muted-foreground font-mono">{new Date(s.started_at).toLocaleString("pt-BR")}</div>
                    {s.serpapi_error && <div className="text-[10px] text-[#DC2626] mt-1" data-testid={`serpapi-error-${s.id}`}>SerpApi: {s.serpapi_error}</div>}
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-xs text-muted-foreground">{s.sources.length} fontes</div>
                  {(s.serpapi_api_calls !== undefined || s.serpapi_cache_hits !== undefined) && (
                    <div className="text-[10px] text-muted-foreground font-mono" title="Consultas SerpApi novas / atendidas por cache">
                      SerpApi: {s.serpapi_api_calls || 0}api · {s.serpapi_cache_hits || 0}cache
                    </div>
                  )}
                  <Badge variant="outline" className="rounded-sm text-xs">{s.result_count} resultados</Badge>
                  <span className={`text-xs font-semibold ${st.color}`}>{st.label}</span>
                </div>
              </div>
            );
          })}
          {!scans.length && <div className="text-sm text-muted-foreground">Nenhuma varredura ainda.</div>}
        </div>
      </Card>
    </div>
  );
}
